import { getMailPaymentAttribution } from "../../utils/mailAttribution.js";
import React, { useEffect } from "react";
import ModalBasic from "./ModalBasic.jsx";
import styled from "styled-components";
import close_button from "../../img/close_button.png";
import config from "../../config.js";
import ModalThankLowGrant from "../Modal/ModalThankLowGrant.jsx";
import useStore from "../../store.js";
import {
  trackNaverBeginCheckout,
  trackNaverPurchase,
} from "../../utils/naverConversion.js";
import { apiFetch } from "../../utils/apiClient.js";
import { waitForCreditCharge } from "../../utils/paymentConfirmation.js";
import {
  buildPaymentReturnUrl,
  clearPendingWebPayment,
  savePendingWebPayment,
} from "../../utils/paymentRedirect.js";

// 판례 검색 1회당 차감되는 로우 개수
const LOW_PER_QUESTION = 10;

// appProductKey는 Flutter 앱의 InAppPurchaseConfig 상품 선택 key와 일치해야 합니다.
// key의 숫자는 실제 지급량이 아니며, 지급량은 백엔드 상품 매핑과 lowCount가 결정합니다.
const PRICE_DATA = [
  {
    lowCount: 10,
    cost: 990,
    appProductKey: "lawlow_30",
  },
  {
    lowCount: 70,
    cost: 5900,
    baseLowCount: 60,
    bonusLowCount: 10,
    appProductKey: "lawlow_190",
  },
  {
    lowCount: 150,
    cost: 11900,
    baseLowCount: 120,
    bonusLowCount: 30,
    appProductKey: "lawlow_400",
  },
];

// ModalBasic 컴포넌트를 세분화한 충전하기 모달 컴포넌트
const ModalPayment = ({ isOpen, onClose, title }) => {
  const { userInfo, updateUserInfo } = useStore();
  const pendingPaymentRef = React.useRef(null);
  const purchaseConfirmationPromiseRef = React.useRef(null);
  const trackedPaymentIdsRef = React.useRef(new Set());
  
  // 결제 성공 모달 상태 관리
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = React.useState(false);
  const [purchasedLowCount, setPurchasedLowCount] = React.useState(0);
  const [isPurchaseConfirming, setIsPurchaseConfirming] = React.useState(false);
  
  const basicProduct = PRICE_DATA[0];

  // 금액으로부터 로우 개수를 반환하는 함수
  const getLowCountFromAmount = (amount) => {
    const data = PRICE_DATA.find((item) => item.cost === amount);
    return data ? data.lowCount : basicProduct.lowCount;
  };

  const getCostFromLowCount = (lowCount) => {
    const data = PRICE_DATA.find((item) => item.lowCount === lowCount);
    return data ? data.cost : null;
  };

  const getCurrentCreditAmount = () => {
    const creditAmount = Number(userInfo?.creditAmount);
    return Number.isFinite(creditAmount) ? creditAmount : null;
  };

  const trackPurchaseAfterCreditConfirmed = async ({ value, id, lowCount, creditBefore }) => {
    if (!value || !id || trackedPaymentIdsRef.current.has(id)) return false;

    const isCreditConfirmed = await waitForCreditCharge({ creditBefore, lowCount });
    if (!isCreditConfirmed) return false;

    trackedPaymentIdsRef.current.add(id);
    await trackNaverPurchase({ value, id });
    return true;
  };

  const beginPurchaseConfirmation = (payment) => {
    setIsPurchaseConfirming(true);

    const confirmationPromise = trackPurchaseAfterCreditConfirmed(payment)
      .catch((error) => {
        console.error("구매 전환 확인 중 오류가 발생했습니다.", error);
        return false;
      })
      .finally(() => {
        if (purchaseConfirmationPromiseRef.current === confirmationPromise) {
          purchaseConfirmationPromiseRef.current = null;
          setIsPurchaseConfirming(false);
        }
      });

    purchaseConfirmationPromiseRef.current = confirmationPromise;
    return confirmationPromise;
  };

  /* 포트원 SDK 설치 - IMP 라이브러리 가져오는 코드 */
  useEffect(() => {
    // 모달이 열릴 때만 스크립트 로드
    if (!isOpen) return;

    const iamport = document.createElement("script");
    iamport.src = "https://cdn.iamport.kr/v1/iamport.js";
    document.head.appendChild(iamport);

    // 플러터 앱에서 결제 완료 후 메시지 수신
    const handleFlutterMessage = (event) => {
      const isLegacyPaymentComplete = event.data?.type === 'payment_complete';
      const isVerifiedNativePayment =
        event.data?.type === 'payment_result' &&
        event.data?.status === 'success' &&
        (event.data?.transaction_id || event.data?.credit_amount);

      if (isLegacyPaymentComplete || isVerifiedNativePayment) {
        const pendingPayment = pendingPaymentRef.current;
        if (!pendingPayment) return;

        // 구버전 앱은 이전 지급량을 전달할 수 있으므로 현재 결제 상품의 지급량을 우선합니다.
        const lowCount =
          pendingPayment.lowCount ||
          getLowCountFromAmount(event.data.amount) ||
          event.data.lowCount;
        const cost =
          pendingPayment.value ||
          event.data.amount ||
          getCostFromLowCount(lowCount);
        const merchantUid =
          event.data.merchantUid ||
          event.data.merchant_uid ||
          event.data.transaction_id ||
          pendingPayment.id;
        setPurchasedLowCount(lowCount);
        beginPurchaseConfirmation({
          value: cost,
          id: merchantUid,
          lowCount,
          creditBefore: pendingPayment.creditBefore,
        });
        
        // 결제 완료 처리 - 결제 모달 닫은 후 성공 모달 표시
        onClose(); // 결제 모달 닫기
        setTimeout(() => {
          setShowPaymentSuccessModal(true); // 성공 모달 표시
        }, 300); // 300ms 지연으로 부드러운 전환
      }
    };

    window.addEventListener('message', handleFlutterMessage);

    return () => {
      document.head.removeChild(iamport);
      window.removeEventListener('message', handleFlutterMessage);
    };
  }, [isOpen]);

  // 플러터 WebView와 웹 브라우저 분기 처리를 위한 결제 함수
  const handleRequestPay = async (amount) => {
    const product = PRICE_DATA.find((item) => item.cost === amount);
    if (!product) return;

    const { appProductKey, lowCount } = product;
    const paymentId = `app_${new Date().getTime()}`;
    pendingPaymentRef.current = {
      id: paymentId,
      value: amount,
      lowCount,
      creditBefore: getCurrentCreditAmount(),
    };

    // 플러터 WebView에서 실행 중인지 확인
    if (window.flutter_inappwebview && window.flutter_inappwebview.callHandler) {
      setPurchasedLowCount(lowCount); // 결제 시도하는 로우 개수 저장
      trackNaverBeginCheckout({ value: amount, id: paymentId });
      window.flutter_inappwebview.callHandler('LawlowApp', `purchase:${appProductKey}`);
    } else if (window.LawlowApp && typeof window.LawlowApp.postMessage === 'function') {
      setPurchasedLowCount(lowCount); // 결제 시도하는 로우 개수 저장
      trackNaverBeginCheckout({ value: amount, id: paymentId });
      window.LawlowApp.postMessage(`purchase:${appProductKey}`);
    } else {
      setPurchasedLowCount(lowCount); // 결제 시도하는 로우 개수 저장
      await requestPay(amount, lowCount);
    }
  };

  // Web 결제 요청 함수(이니시스)
  const requestPay = async (cost, lowCount) => {
    const { IMP } = window;
    const SERVER_URI = config.serverUri;
    const uri = `/private/payments/check`;
    const server_url = `${SERVER_URI}${uri}`;
    const merchant_uid = `mid_${new Date().getTime()}`; // 결제 고유 주문번호
    pendingPaymentRef.current = {
      id: merchant_uid,
      value: cost,
      lowCount,
      creditBefore: getCurrentCreditAmount(),
    };
    trackNaverBeginCheckout({ value: cost, id: merchant_uid });
    
    try {
      const response = await apiFetch(server_url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ merchant_uid, ...getMailPaymentAttribution() }),
      });
      if (!response.ok) {
        throw new Error(`payment check failed: ${response.status}`);
      }
    } catch (error) {
      console.error("결제 요청 등록 실패:", error);
      window.alert("결제 요청을 준비하지 못했습니다. 잠시 후 다시 시도해 주세요.");
      return;
    }

    // 모바일 웹은 PG 리다이렉트로 돌아오므로 결제 시작 정보를 저장해 두고,
    // 결제를 시작한 현재 화면으로 그대로 복귀시킨다.
    savePendingWebPayment(pendingPaymentRef.current);

    IMP.init(`${process.env.REACT_APP_PORTONE_IMP_CODE}`);
    IMP.request_pay(
      {
        pg: "html5_inicis.MOI1362889",
        pay_method: "card",
        merchant_uid: merchant_uid,
        name: `${lowCount} 로우 결제`,
        amount: cost,
        m_redirect_url: buildPaymentReturnUrl(),
      },
      function (rsp) {
        // callback으로 결과를 받은 경우 리다이렉트 복귀 처리가 필요 없다.
        clearPendingWebPayment();
        if (rsp.error_code != null) {
          return alert(`결제에 실패하였습니다. 에러 내용: ${rsp.error_msg}`);
        }
        if (rsp.success) {
          beginPurchaseConfirmation({
            value: cost,
            id: merchant_uid,
            lowCount,
            creditBefore: pendingPaymentRef.current?.creditBefore,
          });
          onClose();
          setTimeout(() => {
            setShowPaymentSuccessModal(true);
          }, 300);
        }
      }
    );
  };

  const handlePaymentSuccessModalClose = async () => {
    const pendingConfirmation = purchaseConfirmationPromiseRef.current;
    if (pendingConfirmation) {
      await pendingConfirmation;
    }

    // 화면을 새로고침하지 않고 스토어의 사용자 정보만 갱신해
    // 결제 전 보던 화면과 입력 상태를 유지한다.
    await updateUserInfo(() => {});
    setShowPaymentSuccessModal(false);
  };

  return (
    <>
      <ModalThankLowGrant
        isOpen={showPaymentSuccessModal}
        onConfirm={handlePaymentSuccessModalClose}
        messageTitle="로우 결제가 완료되었습니다!"
        messageSubtitle={
          isPurchaseConfirming
            ? `${purchasedLowCount}로우 충전을 확인하고 있습니다.`
            : `${purchasedLowCount}로우가 충전되었습니다.`
        }
        confirmText={isPurchaseConfirming ? "확인 중" : "확인"}
        isConfirmDisabled={isPurchaseConfirming}
      />
      <ModalBasic
        isOpen={isOpen}
        onClose={onClose}
        width="min(960px, calc(100vw - 32px))"
        maxHeight="calc(100dvh - 32px)"
        padding="0">
        <WrapperHeader>
          <Header>{title}</Header>
          <CloseButton type="button" onClick={onClose} aria-label="충전 창 닫기">
            <img src={close_button} alt="" width="24" height="24" />
          </CloseButton>
        </WrapperHeader>
        <WrapperContents>
          <Intro>한 번 검색은 10로우예요. 필요한 만큼 충전하고 이어서 확인하세요.</Intro>
          {getCurrentCreditAmount() !== null && (
            <Balance>보유 {getCurrentCreditAmount().toLocaleString("ko-KR")}로우</Balance>
          )}
          <LowBoxWrapper>
            {PRICE_DATA.map((product, index) => (
              <LowBox key={product.lowCount}>
                <ProductName>{["한 번 찾아보기", "여러 번 확인하기", "꾸준히 이용하기"][index]}</ProductName>
                <Price>{product.cost.toLocaleString("ko-KR")}원</Price>
                <QuestionText>판례 검색 {product.lowCount / LOW_PER_QUESTION}회 가능</QuestionText>
                <CreditAmount>{product.lowCount}로우 충전</CreditAmount>
                <Bonus>{product.bonusLowCount ? `${product.bonusLowCount}로우 추가 혜택 포함` : "필요한 검색 한 번부터"}</Bonus>
                <BoxPrice type="button" onClick={() => handleRequestPay(product.cost)}>
                  {product.cost.toLocaleString("ko-KR")}원 충전하기
                </BoxPrice>
              </LowBox>
            ))}
          </LowBoxWrapper>
          <UsageGuide aria-label="기능별 로우 사용량">
            <strong>충전한 로우는 이렇게 써요</strong>
            <span>판례 검색 10로우 · 내 상황과 비교 5로우</span>
            <span>맞춤 문장·카톡 증거 정리 각 2로우</span>
          </UsageGuide>
          <Terms>모든 금액은 부가세 포함 · 1회 결제 · 자동 결제 없음<br />충전한 로우의 유효기간은 구매일로부터 1년입니다.</Terms>
        </WrapperContents>
      </ModalBasic>
    </>
  );
};

const WrapperHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid #eef0ef;
`;

const Header = styled.h2`
  margin: 0;
  color: #111;
  font-size: 22px;
  line-height: 30px;
  word-break: keep-all;
  @media (max-width: 599px) { font-size: 19px; }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #f4f5f4;
  cursor: pointer;
  &:focus-visible { outline: 2px solid #00873e; outline-offset: 3px; }
`;

const WrapperContents = styled.div`
  padding: 24px;
  @media (max-width: 599px) { padding: 16px; }
`;

const Intro = styled.p`
  margin: 0;
  color: #333;
  font-size: 15px;
  font-weight: 500;
  line-height: 24px;
  word-break: keep-all;
`;

const Balance = styled.p`
  margin: 8px 0 0;
  color: #007f3a;
  font-size: 13px;
`;

const LowBoxWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 20px;
  @media (max-width: 719px) { grid-template-columns: 1fr; gap: 12px; }
`;

const LowBox = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 20px;
  border: 1px solid #e0e6e2;
  border-radius: 16px;
  background: #fff;
  @media (max-width: 719px) { padding: 16px; }
`;

const ProductName = styled.h3`
  margin: 0 0 12px;
  color: #333;
  font-size: 15px;
  line-height: 22px;
`;

const Price = styled.div`
  color: #111;
  font-size: 30px;
  font-weight: 700;
  line-height: 38px;
  @media (max-width: 719px) { font-size: 26px; line-height: 32px; }
`;

const QuestionText = styled.div`
  margin-top: 8px;
  color: #222;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
`;

const CreditAmount = styled.div`
  margin-top: 2px;
  color: #59665f;
  font-size: 13px;
  font-weight: 400;
`;

const Bonus = styled.div`
  margin: 8px 0 16px;
  color: #007f3a;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
`;

const BoxPrice = styled.button`
  width: 100%;
  min-height: 48px;
  margin-top: auto;
  padding: 10px;
  border: 0;
  border-radius: 10px;
  background: #111;
  color: #00f370;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  line-height: 22px;
  cursor: pointer;
  &:hover { background: #26352c; }
  &:focus-visible { outline: 2px solid #00873e; outline-offset: 3px; }
`;

const UsageGuide = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f3f8f5;
  color: #46534b;
  font-size: 13px;
  font-weight: 400;
  line-height: 22px;
  word-break: keep-all;
  strong { color: #222; font-weight: 600; }
`;

const Terms = styled.p`
  margin: 16px 0 0;
  color: #59665f;
  font-size: 12px;
  font-weight: 400;
  line-height: 21px;
  word-break: keep-all;
`;

export default ModalPayment;
